<script lang="ts">
  import type { IconValues } from '@gat-ship/icons'
  import type { GatRouteLocation, AppRoute } from '@gat-ship/lib/router'
  import { testIds } from '@gat-ship/types'
  import { cn } from '@gat-ship/vue-components'
  import type { HTMLAttributes } from 'vue'

  export interface PageHeaderProps {
    class?: HTMLAttributes['class']
    title: string
    description?: string
    icon?: IconValues
    backTo?: GatRouteLocation<AppRoute>
    titleTestId?: string
    descriptionTestId?: string
    hideHeader?: boolean
  }
</script>

<script setup lang="ts">
  const props = defineProps<PageHeaderProps>()
</script>

<template>
  <header
    v-if="!props.hideHeader"
    :class="
      cn(
        'flex min-h-[88px] flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between md:gap-3',
        props.class
      )
    "
  >
    <slot name="header">
      <div class="flex flex-1 gap-4 lg:gap-3.5">
        <div
          v-if="props.icon"
          class="shadow-xs ring-muted flex size-12 shrink-0 items-center justify-center rounded-full ring-1 ring-inset"
        >
          <gat-icon :icon="props.icon" class="text-soft size-6" />
        </div>
        <div class="space-y-1">
          <slot name="title">
            <div
              :class="['text-lg font-medium leading-tight']"
              :data-testid="props.titleTestId ?? testIds.common.page.title"
            >
              {{ props.title }}
            </div>
            <div
              class="text-soft whitespace-pre-line text-sm leading-normal"
              :data-testid="
                props.descriptionTestId ?? testIds.common.page.description
              "
            >
              {{ props.description }}
            </div>
          </slot>
        </div>
      </div>
      <div class="hidden items-center gap-3 lg:flex">
        <slot> </slot>
      </div>
    </slot>
  </header>

  <div>
    <div v-if="!props.hideHeader">
      <div
        role="separator"
        class="before:bg-border relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
      ></div>
    </div>
    <div
      v-if="props.backTo"
      class="relative z-50 flex items-center gap-1"
      :class="{
        'pt-4': props.hideHeader,
        '-mb-6 py-3': !props.hideHeader
      }"
    >
      <router-link
        :to="props.backTo"
        class="flex items-center gap-1 text-sm font-medium hover:underline"
      >
        <gat-icon :icon="$icons.common.ArrowLeft" class="size-4 shrink-0" />
        <span v-if="props.backTo.meta?.title">
          Back to {{ props.backTo.meta?.title }}
        </span>
        <span v-else> Go Back </span>
      </router-link>
    </div>
  </div>
</template>
